// Owns what a Shared Setting *is* — its name, the integrations it links and the
// mapping rows — plus loading it for edit, validating it and persisting it.

import { useCallback, useEffect, useState } from 'react';
import noop from 'lodash/noop';
import { t } from '@lingui/macro';
import { useNotifications } from '@decisiv/ui-components';

import SharedSettingsApi from '../../../api/shared_settings';
import IntegrationsApi from '../../../api/integrations';
import {
  errorForAttribute,
  isMappedRow,
  noMappingsMade as hasNoMappings,
  unplacedErrorDetail,
} from './sharedSettingModel';

const MIN_NAME_LENGTH = 3;
const MIN_INTEGRATIONS = 2;

export default function useSharedSettingForm({ editingId, onBack }) {
  const [name, setName] = useState('');
  const [selectedIntegrations, setSelectedIntegrations] = useState([]);
  const [mappings, setMappings] = useState([]);
  const [savedIntegrationIds, setSavedIntegrationIds] = useState(
    () => new Set(),
  );
  const [loading, setLoading] = useState(false);
  const [nameError, setNameError] = useState('');
  const [integError, setIntegError] = useState('');
  const { notify } = useNotifications();

  useEffect(() => {
    if (!editingId) return;

    async function loadEdit() {
      setLoading(true);
      const { data, errors } = await SharedSettingsApi.fetchSharedSetting(
        editingId,
      );

      if (errors || !data) {
        notify(
          {
            intent: 'warning',
            title: t`Failed to load Shared Setting`,
            onClose: noop,
          },
          t`Please try again.`,
        );
        setLoading(false);
        return;
      }

      const existing = { id: data.id, ...data.attributes };
      setName(existing.name);
      setMappings(existing.mappings || []);
      setSavedIntegrationIds(new Set(existing.integration_uuids || []));

      if (existing.integration_uuids?.length > 0) {
        const results = await Promise.all(
          existing.integration_uuids.map((uuid) =>
            IntegrationsApi.fetchIntegrationBasicInfo(uuid),
          ),
        );
        setSelectedIntegrations(
          results.filter((r) => r.data).map((r) => r.data),
        );
      }

      setLoading(false);
    }

    loadEdit();
  }, [editingId, notify]);

  const changeName = useCallback((value) => {
    setName(value);
    setNameError('');
  }, []);

  const addIntegration = useCallback((integration) => {
    setSelectedIntegrations((previous) => [...previous, integration]);
    setIntegError('');
  }, []);

  const removeIntegration = useCallback((uuid) => {
    setSelectedIntegrations((previous) =>
      previous.filter((i) => i.id !== uuid),
    );
    // Dropping the offending integration is how a conflict error gets resolved.
    setIntegError('');
  }, []);

  const noMappingsMade = hasNoMappings(mappings);

  const validate = useCallback(() => {
    let valid = true;

    if (!name.trim()) {
      setNameError(t`Name is required`);
      valid = false;
    }

    if (selectedIntegrations.length < MIN_INTEGRATIONS) {
      setIntegError(t`Select at least 2 integrations`);
      valid = false;
    }

    if (noMappingsMade) valid = false;

    return valid;
  }, [name, selectedIntegrations, noMappingsMade]);

  const save = useCallback(async () => {
    if (!validate()) return;

    const attributes = {
      name: name.trim(),
      integration_uuids: selectedIntegrations.map((i) => i.id),
      mappings: mappings.filter(isMappedRow),
    };

    const response = editingId
      ? await SharedSettingsApi.updateSharedSetting(editingId, attributes)
      : await SharedSettingsApi.createSharedSetting(attributes);

    if (response.errors) {
      if (errorForAttribute(response.errors, 'name')) {
        setNameError(t`Name is already taken`);
      }

      // The API rejects an integration that already belongs to another Shared
      // Setting and names it in the message, so surface it next to the selection.
      const integrationsDetail = errorForAttribute(
        response.errors,
        'integration_uuids',
      )?.detail;
      if (integrationsDetail) setIntegError(integrationsDetail);

      notify(
        {
          intent: 'warning',
          title: t`Failed to save Shared Setting`,
          onClose: noop,
        },
        unplacedErrorDetail(response.errors) ||
          t`Please check the form and try again.`,
      );
      return;
    }

    notify(
      {
        intent: 'success',
        title: editingId
          ? t`Shared Setting updated`
          : t`Shared Setting created`,
        onClose: noop,
      },
      attributes.name,
    );
    onBack();
  }, [
    validate,
    name,
    selectedIntegrations,
    mappings,
    editingId,
    notify,
    onBack,
  ]);

  return {
    name,
    nameError,
    changeName,
    selectedIntegrations,
    integError,
    addIntegration,
    removeIntegration,
    mappings,
    setMappings,
    savedIntegrationIds,
    loading,
    noMappingsMade,
    nameOk: name.trim().length >= MIN_NAME_LENGTH,
    integOk: selectedIntegrations.length >= MIN_INTEGRATIONS,
    save,
  };
}
